import type { SettingsNavigationCustomisationInput } from '@atlassian/jira-relay/src/__generated__/updateNavigationCustomisationMutation.graphql';
import type {
	DisplaySettingsProperties,
	DisplaySettingsPropertyKeysType,
} from '../ui/display-settings-provider/types.tsx';
import {
	DisplaySettingsPropertyKeys,
	DisplaySettingsAllowedValuesMap,
} from '../ui/display-settings-provider/constants.tsx';
import type { NavigationCustomisationNodes } from './types.tsx';

const mapProperties = (
	properties: DisplaySettingsProperties,
): SettingsNavigationCustomisationInput['properties'] => {
	return Object.entries(properties).map(([key, value]) => ({ key, value }));
};

const isDisplaySettingsKey = (value: string): value is keyof typeof DisplaySettingsPropertyKeys => {
	const keys: String[] = Object.values(DisplaySettingsPropertyKeys);
	return keys.includes(value);
};

const displaySettingsValue = <K extends DisplaySettingsPropertyKeysType>(
	key: keyof typeof DisplaySettingsPropertyKeys,
	value: string,
) => {
	const allowedValues: String[] = DisplaySettingsAllowedValuesMap[key];
	if (allowedValues.includes(value)) {
		return allowedValues.find(
			(allowedValue): allowedValue is DisplaySettingsProperties[K] => allowedValue === value,
		);
	}
};

const getNavigationCustomisation = (nodes: NavigationCustomisationNodes) => {
	return nodes?.reduce<Partial<DisplaySettingsProperties>>((customisations, node) => {
		if (node?.key && node?.value && isDisplaySettingsKey(node.key)) {
			const key = DisplaySettingsPropertyKeys[node.key];
			if (displaySettingsValue(key, node.value)) {
				Object.assign(customisations, { [key]: displaySettingsValue<typeof key>(key, node.value) });
			}
		}
		return customisations;
	}, {});
};

export const applyNavigationCustomisation = (
	nodes: NavigationCustomisationNodes,
	properties: DisplaySettingsProperties,
) => {
	const customisations = getNavigationCustomisation(nodes);
	return mapProperties({ ...customisations, ...properties });
};
